import { useState } from "react";
import { ColorPalette } from "@/features/canvas/components/ColorPalette";
import { StrokeWidthSlider } from "@/features/canvas/components/StrokeWidthSlider";
import { ToolGrid } from "@/features/canvas/components/ToolGrid";
import type { SharedColor } from "@/lib/canvas/shared-shape";
import { TELESTRATOR_TOOL_KINDS } from "@/lib/canvas/telestrator-schema";
import {
  useReviewEditorStore,
  type EditorToolKind,
} from "@/stores/review-editor";

/**
 * Floating tool + composer overlay for Coach Review focus mode.
 *
 * Focus mode hides the canvas's own side toolbar (via the canvas's
 * `hideToolbar` prop) and lets the video/board fill the viewport.
 * The coach reveals the rail on demand by clicking the "Tools"
 * pill in the top-right corner. The revealed panel carries BOTH
 * the drawing tools AND the composer (`children`) — the same
 * stack as the inline right rail — so a coach in focus mode can
 * draw AND write the note without leaving focus mode. Mirrors the
 * legacy Replay focus-drawer (see
 * docs/archive/.../07-video-focus-drawer.png: tools at the top,
 * full composer below).
 *
 * Tool state source is the same `useReviewEditorStore` the canvas
 * reads from, so the overlay tools drive the canvas in real time.
 * The composer (`children`) is the SAME `composerElement` node the
 * inline rail renders — but it can only be mounted in one place at
 * a time, so CoachReview passes it here only while focus mode is
 * open (the inline canvas is unmounted then anyway).
 *
 * IMPORTANT — the panel is kept MOUNTED while focus mode is active
 * and only hidden via `hidden` when the "Tools" pill is collapsed.
 * It must NOT be conditionally unmounted: `children` is a live
 * React Hook Form instance, and unmounting it on "Close tools"
 * would destroy the coach's in-progress composer edits (the parent
 * snapshots RHF state on focus CLOSE, not on panel collapse, so an
 * unmount here is silent data loss). The drawing tools are
 * store-backed and stateless, so they ride along harmlessly.
 */

const TACTICAL_TOOLS: readonly EditorToolKind[] = [
  "select",
  "arrow",
  "line",
  "zone",
  "label",
  "freehand",
  "player",
  "ball",
];

const TELESTRATOR_TOOLS_AS_EDITOR: readonly EditorToolKind[] =
  TELESTRATOR_TOOL_KINDS as readonly EditorToolKind[];

/** Caller passes the active CoachReview source ("video" or
 * "tactical_board"). We map "video" → telestrator state and
 * "tactical_board" → tactical state. The mapping mirrors the rest
 * of Coach Review (the SourceToggle's "video" tab mounts
 * `<TelestratorCanvas>`). */
export type FloatingToolOverlayProps = {
  surface: "tactical_board" | "video";
  /** The composer stack (template selector + composer form). Same
   * node the inline right rail renders — rendered here below the
   * drawing tools so focus mode is a complete authoring surface,
   * not draw-only. */
  children?: React.ReactNode;
};

export function FloatingToolOverlay({ surface, children }: FloatingToolOverlayProps) {
  const [open, setOpen] = useState(false);

  const surfaceKey = surface === "tactical_board" ? "tactical" : "telestrator";
  const armedTool = useReviewEditorStore((s) => s[surfaceKey].armedTool);
  const color = useReviewEditorStore((s) => s[surfaceKey].color);
  const strokeWidth = useReviewEditorStore((s) => s[surfaceKey].strokeWidth);
  const armTool = useReviewEditorStore((s) => s.armTool);
  const setColor = useReviewEditorStore((s) => s.setColor);
  const setStrokeWidth = useReviewEditorStore((s) => s.setStrokeWidth);

  const tools = surface === "tactical_board" ? TACTICAL_TOOLS : TELESTRATOR_TOOLS_AS_EDITOR;
  // The store's `surface` field gates `armTool` (falls back to
  // select if a tool isn't allowed on the active surface). The
  // canvas's mount effect sets `surface` correctly before this
  // overlay renders, so no per-overlay surface call is needed.

  return (
    <div
      className="pointer-events-none absolute right-4 top-4 z-[120] flex flex-col items-end gap-2"
      data-testid="coach-review-floating-tools"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="coach-review-floating-tool-panel"
        data-testid="coach-review-floating-tools-toggle"
        className="pointer-events-auto bg-surface/95 backdrop-blur border border-rule text-ink rounded-full px-4 py-2 text-sm font-display uppercase tracking-wide shadow-lg hover:bg-surface transition-colors"
      >
        {open ? "Close tools" : `Tools · ${armedTool}`}
      </button>
      {/* The panel stays MOUNTED (just hidden when collapsed) so
       * `children` — the live RHF composer — is never destroyed by
       * a "Close tools" click. See the component docstring.
       *
       * Collapse uses the Tailwind `hidden` utility (`display:
       * none`), NOT the HTML `hidden` attribute: the panel's open
       * state needs `display: flex`, and the HTML `hidden`
       * attribute (a UA `display: none`) loses to any explicit
       * `display` rule. A `display: none` subtree is already out
       * of the tab order and the a11y tree, so no extra
       * `aria-hidden`/`inert` is needed. */}
      <div
        id="coach-review-floating-tool-panel"
        role="group"
        aria-label="Drawing tools and composer"
        data-testid="coach-review-floating-tool-panel"
        className={
          open
            ? "pointer-events-auto bg-surface/95 backdrop-blur border border-rule rounded-lg p-4 shadow-xl w-80 max-h-[calc(100vh-7rem)] overflow-y-auto flex flex-col gap-4"
            : "hidden"
        }
      >
        <ToolGrid
          tools={tools}
          value={armedTool}
          onChange={(t) => armTool(t)}
        />
        <ColorPalette
          value={color as SharedColor}
          onChange={(c) => setColor(c)}
        />
        <StrokeWidthSlider value={strokeWidth} onChange={(w) => setStrokeWidth(w)} />
        {children ? (
          <div className="flex flex-col gap-4 pt-4 border-t border-rule/40">
            {children}
          </div>
        ) : null}
        <p className="text-xs text-ink-muted">
          Press <kbd className="font-mono">Esc</kbd> to exit focus mode.
        </p>
      </div>
    </div>
  );
}
